import React from "react";

/**
 * Reusable Loading Spinner Component
 * Uses Tailwind CSS for styling with increased default size
 *
 * @param {Object} props - Component props
 * @param {boolean} props.loading - Whether to show the spinner
 * @param {string} props.message - Optional loading message
 * @param {string} props.size - Spinner size ('sm', 'md', 'lg')
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.containerClassName - Container CSS classes
 * @param {boolean} props.center - Whether to center the spinner (default: true)
 * @param {string} props.padding - Vertical padding class (default: 'py-16')
 */
const LoadingSpinner = ({
  loading = true,
  message = "Loading...",
  size = "lg", // 'sm', 'md', 'lg' - now using Tailwind classes
  className = "text-blue-600",
  containerClassName = "",
  center = true,
  padding = "py-16", // Use predefined Tailwind classes
}) => {
  if (!loading) return null;

  // Handle size variants - use Tailwind classes
  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "w-6 h-6";
      case "md":
        return "w-8 h-8";
      case "lg":
        return "w-16 h-16"; // Increased from w-12 h-12
      default:
        return "w-16 h-16"; // Default to larger size
    }
  };

  const sizeClasses = getSizeClasses();

  const spinnerElement = (
    <div
      className={`${sizeClasses} border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin ${className}`}
      role="status"
      aria-label={message}
    >
      <span className="sr-only">{message}</span>
    </div>
  );

  if (center) {
    return <div className={`flex justify-center items-center ${padding} ${containerClassName}`}>{spinnerElement}</div>;
  }

  return spinnerElement;
};

export default LoadingSpinner;
